import { Link } from "react-router-dom";
import {CheckCircle, TrendingUp, Shield, Zap} from 'lucide-react';
import HandicapCalculator from "@/components/HandicapCalculator";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

function Home() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
              Free USGA Course Handicap Calculator & Tracking Tool
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-grade calculators for handicap index, course handicap, and scramble tournaments. USGA-aligned accuracy for unofficial tracking.
            </p>
          </div>

          {/* Calculator Component (Hero Element) */}
          <HandicapCalculator />
        </div>
      </section>

      {/* Why Use This Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
            Why Use Golf Handicap Pro?
          </h2>
          <p className="text-lg text-gray-600 text-center max-w-3xl mx-auto mb-12">
            Our tool provides USGA-aligned accuracy for unofficial tracking, helping golfers find their handicap without immediate GHIN costs.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0FDF4] rounded-full mb-4">
                <CheckCircle className="text-[#006747]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                USGA-Aligned Formulas
              </h3>
              <p className="text-gray-600">
                Calculate with the same formulas used by official GHIN handicap systems
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0FDF4] rounded-full mb-4">
                <Zap className="text-[#006747]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Instant Results
              </h3>
              <p className="text-gray-600">
                Real-time calculations without page refreshes. Get your handicap in seconds
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0FDF4] rounded-full mb-4">
                <TrendingUp className="text-[#006747]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Multiple Calculators
              </h3>
              <p className="text-gray-600">
                Handicap index, course handicap, and scramble tournament tools in one place
              </p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F0FDF4] rounded-full mb-4">
                <Shield className="text-[#006747]" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Free & Accurate
              </h3>
              <p className="text-gray-600">
                No signup required. Professional accuracy for casual tracking and practice
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* GHIN Handicap Formula Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            GHIN Handicap Formula & Calculation
          </h2>
          <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
            <p className="text-lg text-gray-700 mb-6">
              Understanding how your golf handicap is calculated helps you track your progress and improve your game. The GHIN handicap formula uses a standardized approach recognized by the USGA.
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-[#006747] mb-3">
                  Handicap Index Formula
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#006747]">
                  <code className="text-gray-800 font-mono">
                    Handicap Index = (Gross Score - Course Rating) × 113 / Slope Rating
                  </code>
                </div>
                <p className="text-gray-600 mt-3">
                  This formula calculates your score differential, which represents how well you played relative to the course difficulty. The number 113 is the standard slope rating.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#006747] mb-3">
                  USGA Course Handicap Formula
                </h3>
                <div className="bg-gray-50 p-4 rounded-lg border-l-4 border-[#006747]">
                  <code className="text-gray-800 font-mono">
                    Course Handicap = Handicap Index × (Slope Rating / 113) + (Course Rating - Par)
                  </code>
                </div>
                <p className="text-gray-600 mt-3">
                  Your course handicap adjusts your handicap index for the specific course you're playing, accounting for both slope rating and the difference between course rating and par.
                </p>
              </div>

              <div className="bg-[#F0FDF4] p-6 rounded-lg border border-[#22C55E]">
                <h4 className="font-semibold text-gray-900 mb-2">Key Terms:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li>
                    <strong>Course Rating:</strong> The expected score for a scratch golfer (0 handicap)
                  </li>
                  <li>
                    <strong>Slope Rating:</strong> Measures course difficulty for bogey golfers (113 is average)
                  </li>
                  <li>
                    <strong>Score Differential:</strong> Your performance relative to course difficulty
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link
                to="/formulas"
                className="flex-1 bg-[#006747] text-white text-center py-3 px-6 rounded-lg font-semibold hover:bg-[#005239] transition-colors"
              >
                View Full Formula Guide
              </Link>
              <Link
                to="/usga-calculator"
                className="flex-1 bg-white text-[#006747] text-center py-3 px-6 rounded-lg font-semibold border-2 border-[#006747] hover:bg-gray-50 transition-colors"
              >
                Try USGA Calculator
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-12">
            More Golf Handicap Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link
              to="/usga-calculator"
              className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow border-2 border-transparent hover:border-[#006747]"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                USGA Course Handicap Calculator
              </h3>
              <p className="text-gray-600 mb-4">
                Convert your handicap index to a course-specific handicap for any golf course
              </p>
              <span className="text-[#006747] font-medium">
                Calculate Now →
              </span>
            </Link>

            <Link
              to="/scramble-tools"
              className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow border-2 border-transparent hover:border-[#006747]"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Scramble & Tournament Tools
              </h3>
              <p className="text-gray-600 mb-4">
                Calculate team handicaps for 2-man and 4-man scramble golf tournaments
              </p>
              <span className="text-[#006747] font-medium">
                View Tools →
              </span>
            </Link>

            <Link
              to="/9-hole-calculator"
              className="bg-gray-50 p-6 rounded-xl hover:shadow-lg transition-shadow border-2 border-transparent hover:border-[#006747]"
            >
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                9-Hole Golf Handicap
              </h3>
              <p className="text-gray-600 mb-4">
                Quick mobile calculator optimized for 9-hole rounds on the course
              </p>
              <span className="text-[#006747] font-medium">
                Calculate Now →
              </span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Home;
